import React, {useMemo} from "react";
import {useOutletContext} from "react-router";
import {IconButton, Typography, useTheme} from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import {ArrowUpward, RotateLeft} from "@mui/icons-material";
import {config} from "../../config";
import {isUserAdmin} from "../../utils";

export const DraftManagersTable = ({loading, rosterData, nominatingManager, incrementNominatingManager, refreshPage, draftIsOver}) => {
    const {currentAuction} = useOutletContext();
    const theme = useTheme();
    const panelTheme = theme.palette.contentPanel;

    const headerCells = useMemo(() => {
        return [
            <TableCell key="topLeftCell">
                <Typography variant="h6" color="inherit" display="inline">{currentAuction.season_year} Draft</Typography>
                <IconButton color="inherit" style={{height: 25, marginLeft: 0, marginBottom: 5}} onClick={refreshPage}><RotateLeft/></IconButton>
            </TableCell>
        ];
    }, [currentAuction.season_year, refreshPage]);

    const tableRows = useMemo(() => {
        const hasRemainingRosterSpots = (managerName) => {
            return rosterData[managerName] ? config.maxRosterSize - rosterData[managerName].rosterSize > 0 : false;
        };

        const hasTooManyPlayers = (managerName) => {
            return rosterData[managerName] ? config.maxRosterSize - rosterData[managerName].rosterSize < 0 : false;
        };

        const hasRemainingBudget = (managerName) => {
            return rosterData[managerName] ? rosterData[managerName].available > 0 : false;
        };

        const isOverBudget = (managerName) => {
            return rosterData[managerName] ? rosterData[managerName].available < 0 : false;
        };

        const determineRosterCellColor = (managerName) => {
            return hasRemainingRosterSpots(managerName) ? 'inherit' : hasTooManyPlayers(managerName) ? '#99000060' : '#99000025';
        };

        const determineBudgetCellColor = (managerName) => {
            return hasRemainingBudget(managerName) ? 'inherit' : isOverBudget(managerName) ? '#99000060' : '#99000025'
        };

        config.managerNames.forEach((managerName) => {
            headerCells.push(<TableCell key={`${managerName}Cell`} style={{textAlign: 'center'}}>{managerName}</TableCell>);
        });

        const nominatingManagerCells = [];
        config.managerNames.forEach((managerName) => {
            nominatingManagerCells.push(<TableCell key={`${managerName}NomCell`} style={{textAlign: 'center'}}>
                {nominatingManager === managerName && <ArrowUpward/>}
            </TableCell>);
        });

        const remainingRosterSpotCells = [];
        config.managerNames.forEach((managerName) => {
            remainingRosterSpotCells.push(<TableCell key={`${managerName}RosterCell`} style={{
                textAlign: 'center',
                backgroundColor: determineRosterCellColor(managerName)
            }}>
                {rosterData[managerName] ? config.maxRosterSize - rosterData[managerName].rosterSize : '?'}
            </TableCell>);
        });

        const remainingBudgetCells = [];
        config.managerNames.forEach((managerName) => {
            remainingBudgetCells.push(<TableCell key={`${managerName}BudgetCell`} style={{
                textAlign: 'center',
                backgroundColor: determineBudgetCellColor(managerName)
            }}>
                {rosterData[managerName] ? rosterData[managerName].available : '?'}
            </TableCell>);
        });

        const rows = [
            <TableRow key="nomRow" style={{height: 45}}>
                <TableCell>
                    Turn to nominate
                    {isUserAdmin() &&
                        <Button variant="contained" color="primary" display="inline" style={{marginLeft: 20, height: 25}}
                                onClick={incrementNominatingManager}>Skip</Button>
                    }
                </TableCell>
                {nominatingManagerCells}
            </TableRow>,
            <TableRow key="rosterRow" style={{height: 45}}>
                <TableCell>Remaining roster spots</TableCell>
                {remainingRosterSpotCells}
            </TableRow>,
            <TableRow key="budgetRow" style={{height: 45}}>
                <TableCell>Remaining budget</TableCell>
                {remainingBudgetCells}
            </TableRow>
        ];

        if (draftIsOver) {
            console.log('...adding...');
            rows.push(<TableRow key="WDE" style={{height: 70}}>
                <TableCell colSpan={9} style={{textAlign: 'center', color: 'red'}}><Typography variant="h4">WORST DRAFT EVER</Typography></TableCell>
            </TableRow>);
        }

        return rows;
    }, [headerCells, nominatingManager, incrementNominatingManager, rosterData, draftIsOver]);

    return (
        <div style={{marginTop: 30, marginBottom: 20}}>
            {loading && <LinearProgress color="secondary"/>}
            <TableContainer style={{...panelTheme}}>
                <Table size="small">
                    <TableHead>
                        <TableRow key="headerRow">
                            {headerCells}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}